import React, { useCallback, useEffect, useState } from "react";
import { adminApi, ADMIN_API_BASE } from "@/lib/adminApi";
import { BadgeCheck, ShieldCheck, X, Clock } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
];

export default function AdminVerifications() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [pending, setPending] = useState(0);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/verifications", { params: { status: tab, limit: 100 } });
            setItems(data.items || []);
            setPending(data.pending || 0);
        } catch (e) {
            toast.error("Couldn't load verification queue");
        } finally {
            setLoading(false);
        }
    }, [tab]);
    useEffect(() => { load(); }, [load]);

    const act = async (userId, action) => {
        let reason = null;
        if (action === "reject") {
            reason = window.prompt("Reason for rejection (shown to the user):", "Please re-record a clear, well-lit selfie video.");
            if (reason === null) return;
        }
        setActing(userId);
        try {
            await adminApi.post(`/admin/verifications/${userId}/action`, { action, reason });
            toast.success(action === "approve" ? "Verified ✓" : "Rejected");
            load();
        } catch (e) {
            toast.error("Action failed");
        } finally {
            setActing(null);
        }
    };

    return (
        <div data-testid="admin-verifications-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black flex items-center gap-2">
                        <BadgeCheck className="h-7 w-7 text-[#E85D5D]" /> Verification queue
                    </h1>
                    <p className="text-sm text-white/50">{pending} awaiting review</p>
                </div>
            </div>

            <div className="mt-4 inline-flex rounded-2xl border border-white/10 bg-white/5 p-1">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`admin-verif-tab-${t.key}`}
                        className={`px-4 py-1.5 rounded-xl text-sm font-bold transition ${
                            tab === t.key ? "bg-[#E85D5D] text-white" : "text-white/60 hover:text-white"
                        }`}
                    >
                        {t.label}
                        {t.key === "pending" && pending > 0 && (
                            <span className="ml-1.5 rounded-full bg-white/20 px-1.5 text-[10px]">{pending}</span>
                        )}
                    </button>
                ))}
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-verif-list">
                {loading ? (
                    <div className="text-white/50 text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-white/10 p-8 text-center text-white/50 col-span-full">
                        <div className="text-4xl mb-2">✅</div>
                        Nothing here.
                    </div>
                ) : items.map((u) => (
                    <div key={u.user_id} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`admin-verif-row-${u.user_id}`}>
                        <div className="aspect-video bg-black grid place-items-center">
                            {u.has_video ? (
                                <video
                                    src={`${ADMIN_API_BASE}${u.video_url}`}
                                    controls
                                    playsInline
                                    className="h-full w-full object-contain"
                                    data-testid={`admin-verif-video-${u.user_id}`}
                                />
                            ) : (
                                <div className="text-white/40 text-xs">No video</div>
                            )}
                        </div>
                        <div className="p-4">
                            <div className="flex items-center gap-2">
                                <div className="font-bold">{u.name || "—"}</div>
                                {u.age ? <span className="text-white/50 text-sm">· {u.age}</span> : null}
                                {u.city ? <span className="text-white/50 text-sm">· {u.city}</span> : null}
                            </div>
                            <div className="text-xs text-white/40 truncate">{u.email}</div>
                            {u.verification_submitted_at && (
                                <div className="mt-1 text-[11px] text-white/40 flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> {(u.verification_submitted_at || "").slice(0, 16).replace("T", " ")}
                                </div>
                            )}
                            {u.verification_status === "rejected" && u.verification_reject_reason && (
                                <div className="mt-2 text-xs text-[#F59E0B] italic">— {u.verification_reject_reason}</div>
                            )}

                            {u.verification_status === "pending" ? (
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => act(u.user_id, "approve")}
                                        disabled={acting === u.user_id}
                                        data-testid={`admin-verif-approve-${u.user_id}`}
                                        className="flex-1 h-9 rounded-lg bg-[#22C55E]/20 border border-[#22C55E]/40 hover:bg-[#22C55E]/30 text-[#22C55E] text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
                                    >
                                        <ShieldCheck className="h-3.5 w-3.5" /> Approve
                                    </button>
                                    <button
                                        onClick={() => act(u.user_id, "reject")}
                                        disabled={acting === u.user_id}
                                        data-testid={`admin-verif-reject-${u.user_id}`}
                                        className="flex-1 h-9 rounded-lg bg-[#EF4444]/20 border border-[#EF4444]/40 hover:bg-[#EF4444]/30 text-[#EF4444] text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
                                    >
                                        <X className="h-3.5 w-3.5" /> Reject
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-3 flex items-center gap-2">
                                    <span
                                        className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${
                                            u.verification_status === "approved"
                                                ? "bg-[#22C55E]/20 text-[#22C55E]"
                                                : "bg-[#EF4444]/20 text-[#EF4444]"
                                        }`}
                                    >
                                        {u.verification_status}
                                    </span>
                                    {u.verification_status === "rejected" && (
                                        <button
                                            onClick={() => act(u.user_id, "approve")}
                                            disabled={acting === u.user_id}
                                            data-testid={`admin-verif-approve-${u.user_id}`}
                                            className="h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 px-3 text-xs font-semibold"
                                        >
                                            Approve anyway
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
